import React, { useEffect, useState } from 'react';
import Modal from '../../ui/Modal';
import ButtonNew from '../../ui/ButtonNew';
import Loader from '../../ui/Loader';
import { getTokensForBudget } from '../../apis/general/general';
import { ethers } from 'ethers';
import { getTokenBalance, ERC20_ABI } from '../../apis/geenpills/mintNFT';
import { getRewardAmount, updateRewardToken } from '../../apis/bounties/bounties';
import { useSDK } from '@metamask/sdk-react';
import { toast } from 'sonner';
import { toNormalRepresentation } from '../../helpers/convertIntoDecimals';
import axios from 'axios';
import celoImg from '../../assets/chains/celo.png';
import optimismImg from '../../assets/chains/optimism.png';
import solanaImg from '../../assets/chains/solana.png';
import baseImg from '../../assets/chains/base.png';
import arbitrumImg from '../../assets/chains/arbitrum.png';

const CHAIN_MAPPINGS = {
    'arbitrum': ['arbitrum'],
    'base': ['base'],
    'optimism': ['optimism'],
    'celo': ['celo'],
    'ethereum': ['ethereum', 'eth'],
    'optimism sepolia': ['optimism sepolia'],
    'base sepolia': ['base sepolia']
};

// Map symbols/names to CoinGecko IDs
const COINGECKO_ID_MAP = {
    'eth': 'ethereum',
    'weth': 'weth',
    'usdc': 'usd-coin',
    'usdt': 'tether',
    'dai': 'dai',
    'matic': 'matic-network',
    'op': 'optimism',
    'arb': 'arbitrum',
    'celo': 'celo',
    'base': 'base', // Base might not have a direct token ID like this, usually it's ETH on Base
    'link': 'chainlink',
    'uni': 'uniswap',
    'aave': 'aave'
};

// Helper function to construct network options from tokens
const constructNetworkOptions = (tokens) => {
    if (!tokens || tokens.length === 0) return [];

    // Extract unique chains from tokens
    const uniqueChains = new Map();

    tokens.forEach(token => {
        if (token.chainImage && token.chainName) {
            // Normalize chain name (trim whitespace and convert to lowercase for key)
            const normalizedKey = token.chainName.trim().toLowerCase();

            // Exclude Solana
            if (normalizedKey === 'solana') return;

            // Only add if not already exists (first occurrence wins)
            if (!uniqueChains.has(normalizedKey)) {
                uniqueChains.set(normalizedKey, {
                    chainName: token.chainName.trim(),
                    chainImage: token.chainImage
                });
            }
        }
    });

    return Array.from(uniqueChains.values());
};

// Helper function to get chain image
const getChainImage = (chainName) => {
    switch (chainName.toLowerCase()) {
        case 'celo':
            return celoImg;
        case 'optimism':
            return optimismImg;
        case 'solana':
            return solanaImg;
        case 'base':
            return baseImg;
        case 'arbitrum':
            return arbitrumImg;
        default:
            return null;
    }
};

const InsufficientBalanceModal = ({ close, bountyId, currentToken, account, provider, totalPayRequired }) => {
    const [tokens, setTokens] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedChain, setSelectedChain] = useState(currentToken?.chainName?.toLowerCase() || 'arbitrum');
    const [selectedToken, setSelectedToken] = useState(null);
    const [balances, setBalances] = useState({});
    const [prices, setPrices] = useState({});
    const [requiredAmounts, setRequiredAmounts] = useState({});
    const [updating, setUpdating] = useState(false);
    const [checkingBalance, setCheckingBalance] = useState(false);
    const [networkOptions, setNetworkOptions] = useState([]);

    useEffect(() => {
        const fetchTokens = async () => {
            try {
                setLoading(true);
                const allTokens = await getTokensForBudget();
                if (Array.isArray(allTokens)) {
                    setTokens(allTokens);
                    const options = constructNetworkOptions(allTokens);
                    setNetworkOptions(options);

                    // Initialize selected token if current token is in the list
                    if (currentToken) {
                        const match = allTokens.find(t => t._id === currentToken._id || (t.address === currentToken.address && t.chainId === currentToken.chainId));
                        if (match) setSelectedToken(match);
                    }
                }
            } catch (error) {
                console.error("Failed to fetch tokens", error);
                toast.error("Failed to fetch token list");
            } finally {
                setLoading(false);
            }
        };
        fetchTokens();
    }, [currentToken]);

    // Fetch balances and prices for tokens in the selected chain
    useEffect(() => {
        const fetchBalancesAndPrices = async () => {
            if (!account || !tokens.length) return;

            const tokensInChain = tokens.filter(t => {
                const tokenChain = (t.chainName || '').toLowerCase();
                const mappings = CHAIN_MAPPINGS[selectedChain] || [selectedChain];
                const match = mappings.some(mapping => tokenChain.includes(mapping));
                if (match && !selectedChain.includes('sepolia') && tokenChain.includes('sepolia')) {
                    return false;
                }
                return match;
            });

            console.log('Tokens in chain:', tokensInChain.map(t => t.symbol));

            // 1. Fetch Balances
            const newBalances = { ...balances };

            await Promise.all(tokensInChain.map(async (token) => {
                if (newBalances[token._id] !== undefined) return; // Already fetched

                try {
                    // Always use getTokenBalance with chainId to ensure we're querying the correct chain
                    const balance = await getTokenBalance(token.chainId, token.address, account);
                    const formattedBalance = Number(balance) / (10 ** token.decimals);
                    newBalances[token._id] = formattedBalance;
                } catch (err) {
                    console.error(`Failed to fetch balance for ${token.symbol} on chain ${token.chainId}`, err);
                    newBalances[token._id] = 0;
                }
            }));
            setBalances(newBalances);

            // 2. Fetch Prices
            const idsToFetch = tokensInChain
                .map(t => COINGECKO_ID_MAP[t.symbol.toLowerCase()] || t.name.toLowerCase().replace(/\s+/g, '-'))
                .filter((v, i, a) => a.indexOf(v) === i); // Unique IDs

            console.log('Fetching prices for IDs:', idsToFetch);

            if (idsToFetch.length > 0) {
                try {
                    const response = await axios.get(`https://api.coingecko.com/api/v3/simple/price?ids=${idsToFetch.join(',')}&vs_currencies=usd`);
                    console.log('Price response:', response.data);
                    setPrices(prev => ({ ...prev, ...response.data }));
                } catch (error) {
                    console.error("Failed to fetch prices", error);
                }
            }
        };

        if (selectedChain) {
            fetchBalancesAndPrices();
        }
    }, [selectedChain, tokens, account]);

    // Fetch required amount when a token is selected
    useEffect(() => {
        const fetchRequired = async () => {
            if (!selectedToken || !bountyId) return;

            if (requiredAmounts[selectedToken._id]) return; // Already fetched

            try {
                setCheckingBalance(true);
                const response = await getRewardAmount(bountyId, selectedToken._id);
                console.log(response, "see this is the response for token selected required")
                if (response?.totalRewardAmount) {
                    setRequiredAmounts(prev => ({
                        ...prev,
                        [selectedToken._id]: response?.totalRewardAmount
                    }));
                }
            } catch (err) {
                console.error("Failed to fetch required amount", err);
            } finally {
                setCheckingBalance(false);
            }
        };

        fetchRequired();
    }, [selectedToken, bountyId]);

    const handleChainSelect = (chainName) => {
        setSelectedChain(chainName.toLowerCase());
        setSelectedToken(null); // Reset token selection when chain changes
    };

    const handleTokenSelect = (token, usdValue) => {
        setSelectedToken(token);
    };

    const handleConfirm = async () => {
        if (!selectedToken) return;

        try {
            setUpdating(true);
            const response = await updateRewardToken(bountyId, selectedToken._id);
            console.log(response, "see this is the response for update reward token")
            if (response) {
                close();
                window.location.reload();
            }
        } catch (err) {
            console.error("Failed to update reward token", err);
        } finally {
            setUpdating(false);
        }
    };

    const rawFilteredTokens = tokens.filter(token => {
        const name = (token.chainName || '').toLowerCase();
        const mappings = CHAIN_MAPPINGS[selectedChain] || [selectedChain];
        const match = mappings.some(m => name.includes(m));
        if (match && !selectedChain.includes('sepolia') && name.includes('sepolia')) {
            return false;
        }
        return match;
    });

    const filteredTokens = Object.values(rawFilteredTokens.reduce((acc, token) => {
        const symbol = token.symbol;
        if (!acc[symbol]) {
            acc[symbol] = token;
        } else {
            const currentBalance = balances[acc[symbol]._id] || 0;
            const newBalance = balances[token._id] || 0;
            if (newBalance > currentBalance) {
                acc[symbol] = token;
            }
        }
        return acc;
    }, {}));

    const isBalanceSufficient = () => {
        if (!selectedToken) return false;
        const balance = balances[selectedToken._id] || 0;
        const required = requiredAmounts[selectedToken._id] || 0;
        return balance >= required;
    };
    console.log(selectedToken, requiredAmounts, balances[selectedToken?._id], tokens, filteredTokens, "this is the selected token")
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]" onClick={(e) => e.target === e.currentTarget && close()}>
            <div className="bg-grayBgNormal rounded-lg p-6 w-[800px] max-h-[90vh] overflow-y-auto">
                <div className="mb-6">
                    <h2 className="text-xl font-bold text-textPrimary">Insufficient Balance, Change Reward Token</h2>
                    <p className="text-textSecondary text-sm mt-1">You have insufficient funds for the chosen reward token, please select a new reward token.</p>
                </div>

                <div className="grid grid-cols-12 gap-6">
                    {/* Chain Selection */}
                    <div className="col-span-4 border border-borderUnderline rounded-lg p-2">
                        <h3 className="text-sm font-semibold text-textPrimary mb-2 px-2">Chain *</h3>
                        <div className="space-y-1">
                            {networkOptions.map(option => (
                                <div
                                    key={option.chainName}
                                    onClick={() => handleChainSelect(option.chainName)}
                                    className={`flex items-center gap-2 p-2 rounded cursor-pointer ${selectedChain === option.chainName.toLowerCase() ? 'bg-neutral50E' : 'hover:bg-neutral50C'}`}
                                >
                                    <img
                                        src={option.chainImage || getChainImage(option.chainName)}
                                        alt={option.chainName}
                                        className="w-5 h-5 rounded-full"
                                    />
                                    <span className="capitalize text-textPrimary">{option.chainName}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Token Selection */}
                    <div className="col-span-8 border border-borderUnderline rounded-lg p-2">
                        <h3 className="text-sm font-semibold text-textPrimary mb-2 px-2">Token *</h3>
                        <div className="space-y-1 max-h-[17rem] overflow-y-auto">
                            {loading ? (
                                <div className="flex justify-center p-4"><Loader /></div>
                            ) : filteredTokens.map(token => {
                                const balance = balances[token._id];
                                const isSelected = selectedToken?._id === token._id;

                                // Get Price
                                const coingeckoId = COINGECKO_ID_MAP[token.symbol.toLowerCase()] || token.name.toLowerCase().replace(/\s+/g, '-');
                                const price = prices[coingeckoId]?.usd || 0;
                                const usdValue = balance !== undefined ? (balance * price).toFixed(2) : '0.00';

                                return (
                                    <div
                                        key={token._id}
                                        onClick={() => handleTokenSelect(token, usdValue)}
                                        className={`flex items-center gap-6 p-2 rounded cursor-pointer ${isSelected ? 'bg-blueBrand2 border border-blueBrand6' : 'hover:bg-neutral50C'}`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <img src={token.image} alt={token.symbol} className="w-6 h-6 rounded-full" />
                                            <span className="font-medium text-textPrimary">{token.symbol}</span>
                                        </div>
                                        <div className="text-right text-sm">
                                            <div className="text-textPrimary">
                                                {balance !== undefined ? (
                                                    <>
                                                        {balance.toFixed(4)} <span className="text-textSecondary">(${usdValue})</span>
                                                    </>
                                                ) : <span className="text-textTertiary">...</span>}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="mt-6 flex items-center justify-between">
                    <div>
                        <p className="text-sm text-textSecondary">Reward Amount</p>
                        <p className="text-2xl font-bold text-textPrimary">
                            {(() => {
                                const dollarValue = totalPayRequired || 0;
                                // Show "<$0.01" for very small values
                                if (dollarValue < 0.01 && dollarValue > 0) {
                                    return "<$0.01";
                                }
                                return `$${dollarValue.toFixed(2)}`;
                            })()}
                        </p>
                    </div>
                    <ButtonNew
                        onClick={handleConfirm}
                        disabled={updating || checkingBalance || !isBalanceSufficient()}
                        variant="blue"
                        size="md"
                        shape="rounded"
                        fixedMinWidth={true}
                    >
                        {updating ? <Loader /> : 'Confirm'}
                    </ButtonNew>
                </div>
            </div>
        </div>
    );
};

export default InsufficientBalanceModal;
